import React, { useEffect, useMemo, useState } from "react";
import { statsClientes } from "../api";
import "./ClienteMesTab.css";

function fmtDias(d) {
  if (d === null || d === undefined) return "—";
  return `${d}d`;
}

function Badge({ tipo }) {
  const style = {
    display: "inline-block",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    border: "1px solid #ddd",
    background: "#f7f7f7",
  };

  if (tipo === "nuevo_sin_seguimiento") return <span style={style}>🆕 Nuevo</span>;
  if (tipo === "riesgo") return <span style={style}>⚠️ Riesgo</span>;
  if (tipo === "inactivo") return <span style={style}>🧊 Inactivo</span>;
  return <span style={style}>✅ Activo</span>;
}

export default function ClientesMesTab({ mes, setMes }) {
  const [creados, setCreados] = useState([]);
  const [citas, setCitas] = useState([]);

  const [nuevos, setNuevos] = useState([]);
  const [riesgo, setRiesgo] = useState([]);
  const [inactivos, setInactivos] = useState([]);
  const [activos, setActivos] = useState([]);

  const [totalCreados, setTotalCreados] = useState(0);
  const [totalCitas, setTotalCitas] = useState(0);

  const [totalNuevos, setTotalNuevos] = useState(0);
  const [totalRiesgo, setTotalRiesgo] = useState(0);
  const [totalInactivos, setTotalInactivos] = useState(0);
  const [totalActivos, setTotalActivos] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI extra (simple)
  const [q, setQ] = useState("");
  const [umbralDias, setUmbralDias] = useState(45);
  const [nuevoDias, setNuevoDias] = useState(14);

  // Pestañas simples (sin librerías)
  const [tab, setTab] = useState("control"); // control | creados | citas

  useEffect(() => {
    if (!mes) return;

    setLoading(true);

    // si tu statsClientes solo acepta (mes), puedes ampliar a statsClientes(mes, {umbral_dias, nuevo_dias})
    // o meter params en la URL dentro del api helper.
    statsClientes(mes, { umbral_dias: umbralDias, nuevo_dias: nuevoDias })
      .then((data) => {
        // lo de antes
        setCreados(data.creados?.items || []);
        setTotalCreados(data.creados?.total || 0);
        setCitas(data.citas?.items || []);
        setTotalCitas(data.citas?.total || 0);

        // nuevo control (si existe)
        setNuevos(data.nuevos_sin_seguimiento?.items || []);
        setTotalNuevos(data.nuevos_sin_seguimiento?.total || 0);

        setRiesgo(data.riesgo?.items || []);
        setTotalRiesgo(data.riesgo?.total || 0);

        setInactivos(data.inactivos?.items || []);
        setTotalInactivos(data.inactivos?.total || 0);

        setActivos(data.activos?.items || []);
        setTotalActivos(data.activos?.total || 0);

        setError("");
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [mes, umbralDias, nuevoDias]);

  const match = (item) => {
    if (!q.trim()) return true;
    const s = `${item.nombre || ""} ${item.apellidos || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  };

  const nuevosFiltrados = useMemo(() => nuevos.filter(match), [nuevos, q]);
  const riesgoFiltrado = useMemo(() => riesgo.filter(match), [riesgo, q]);
  const inactivosFiltrados = useMemo(() => inactivos.filter(match), [inactivos, q]);
  const activosFiltrados = useMemo(() => activos.filter(match), [activos, q]);

  const TablaControl = ({ titulo, items, tipo }) => (
    <>
      <h4 style={{ marginTop: "1.2rem" }}>
        {titulo} ({items.length})
      </h4>

      <div className="pv-table-wrapper">
        <table className="pv-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Estado</th>
              <th>Última sesión</th>
              <th>Días</th>
              <th>30d</th>
              <th>90d</th>
              <th>Score</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.patient_id || c.id}>
                <td>
                  {c.nombre} {c.apellidos}
                </td>
                <td>
                  <Badge tipo={c.categoria || tipo} />
                </td>
                <td>{c.ultima_sesion || "—"}</td>
                <td>{fmtDias(c.dias_desde_ultima)}</td>
                <td>{c.sesiones_30d ?? "—"}</td>
                <td>{c.sesiones_90d ?? "—"}</td>
                <td style={{ fontWeight: 700 }}>{c.score_riesgo ?? "—"}</td>
                <td style={{ maxWidth: 320 }}>{c.motivo || c.motivo_riesgo || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "#777" }}>
                  No hay resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className="clientes-tab">
      <h3>👥 Clientes — {mes}</h3>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "end" }}>
        <label>
          Mes:
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} />
        </label>

        <label>
          Buscar:
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nombre o apellidos..."
          />
        </label>

        <label>
          Umbral (días):
          <input
            type="number"
            min={1}
            max={365}
            value={umbralDias}
            onChange={(e) => setUmbralDias(Number(e.target.value))}
            style={{ width: 90 }}
          />
        </label>

        <label>
          Nuevo (días):
          <input
            type="number"
            min={1}
            max={60}
            value={nuevoDias}
            onChange={(e) => setNuevoDias(Number(e.target.value))}
            style={{ width: 90 }}
          />
        </label>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <button
          className={tab === "control" ? "btn-tab-active" : "btn-tab"}
          onClick={() => setTab("control")}
        >
          🧭 Control
        </button>
        <button
          className={tab === "creados" ? "btn-tab-active" : "btn-tab"}
          onClick={() => setTab("creados")}
        >
          🆕 Creados ({totalCreados})
        </button>
        <button
          className={tab === "citas" ? "btn-tab-active" : "btn-tab"}
          onClick={() => setTab("citas")}
        >
          📅 Con sesiones ({totalCitas})
        </button>
      </div>

      {loading ? (
        <p>Cargando...</p>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          {tab === "control" && (
            <>
              {/* Resumen rápido */}
              <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div className="pv-card">🆕 Nuevos: <strong>{totalNuevos}</strong></div>
                <div className="pv-card">⚠️ Riesgo: <strong>{totalRiesgo}</strong></div>
                <div className="pv-card">🧊 Inactivos: <strong>{totalInactivos}</strong></div>
                <div className="pv-card">✅ Activos: <strong>{totalActivos}</strong></div>
              </div>

              <TablaControl
                titulo="🆕 Nuevos sin seguimiento"
                items={nuevosFiltrados}
                tipo="nuevo_sin_seguimiento"
              />
              <TablaControl
                titulo="⚠️ En riesgo (prioridad contacto)"
                items={riesgoFiltrado}
                tipo="riesgo"
              />
              <TablaControl
                titulo="🧊 Inactivos"
                items={inactivosFiltrados}
                tipo="inactivo"
              />
              <TablaControl
                titulo="✅ Activos"
                items={activosFiltrados}
                tipo="activo"
              />
            </>
          )}

          {tab === "creados" && (
            <>
              <h4>🆕 Clientes creados en el mes ({totalCreados})</h4>
              <div className="pv-table-wrapper">
                <table className="pv-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Fecha creación</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creados.filter(match).map((c) => (
                      <tr key={c.id}>
                        <td>
                          {c.nombre} {c.apellidos}
                        </td>
                        <td>{c.creado}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "citas" && (
            <>
              <h4 style={{ marginTop: "1rem" }}>
                📅 Clientes con sesiones en el mes ({totalCitas})
              </h4>

              <div className="pv-table-wrapper">
                <table className="pv-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Nº sesiones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {citas.filter(match).map((c) => (
                      <tr key={c.patient_id}>
                        <td>
                          {c.nombre} {c.apellidos}
                        </td>
                        <td>{c.sesiones}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}